import React from 'react';
import { Button } from '@/components/ui/button';
import { SuggestedResponse } from '@/types/agentTypes';

interface SuggestedResponseButtonsProps {
  suggestions: SuggestedResponse[];
  onSelect: (suggestion: SuggestedResponse) => void;
  className?: string;
}

/**
 * Component that renders clickable suggestion buttons for agent responses
 */
const SuggestedResponseButtons: React.FC<SuggestedResponseButtonsProps> = ({
  suggestions,
  onSelect,
  className = ''
}) => {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-2 mt-2 ${className}`}>
      {suggestions.map((suggestion) => (
        <Button
          key={suggestion.id}
          variant="outline"
          size="sm"
          className="bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100 hover:text-violet-800"
          onClick={() => onSelect(suggestion)}
        >
          {suggestion.text}
        </Button>
      ))}
    </div>
  );
};

/**
 * Component that renders a single suggested response button
 */
export const SuggestedResponseButton: React.FC<{
  suggestion: SuggestedResponse;
  onSelect: (suggestion: SuggestedResponse) => void;
  className?: string;
  variant?: 'default' | 'outline' | 'action';
}> = ({
  suggestion,
  onSelect,
  className = '',
  variant = 'outline'
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'default':
        return 'bg-violet-500 text-white hover:bg-violet-600';
      case 'outline':
        return 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100 hover:text-violet-800';
      case 'action':
        return 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800';
      default:
        return 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100 hover:text-violet-800';
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className={`${getVariantClasses()} ${className}`}
      onClick={() => onSelect(suggestion)}
    >
      {suggestion.text}
    </Button>
  );
};

export default SuggestedResponseButtons;